<template>
  <div class="tw-w-full tw-p-4 tw-h-[100px] tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-flex tw-flex-col tw-justify-between">
    
    <!-- Loading State -->
    <div v-if="loading" class="tw-flex tw-flex-col tw-justify-between tw-h-full">
      <div class="tw-h-4 tw-w-3/4 tw-bg-gray-200 tw-animate-pulse tw-rounded"></div>
      <div class="tw-flex tw-items-center tw-justify-center tw-flex-1">
        <icon icon="eos-icons:three-dots-loading" class="tw-text-3xl tw-text-gray-400" />
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-full tw-text-center">
      <icon icon="material-symbols:error-outline" class="tw-text-red-500 tw-text-2xl tw-mb-1" />
      <p class="tw-text-xs tw-text-red-500 tw-px-1">{{ error }}</p>
    </div>

    <!-- Loaded State -->
    <div v-else class="tw-flex tw-flex-col tw-justify-between tw-h-full">
      <div>
        <p class="tw-text-sm tw-font-medium tw-text-gray-500">{{ $t('common.user-ranking') }}</p>
      </div>
      
      <div class="tw-flex-1 tw-flex tw-flex-col tw-justify-center tw-py-1">
                 <!-- Current User Rank Display -->
         <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
           <div class="tw-flex tw-items-center tw-gap-2">
             <div class="tw-flex tw-items-center tw-gap-1">
               <span class="tw-text-lg tw-font-bold tw-text-gray-800">#{{ currentUserRank }}</span>
               <icon v-if="currentUserRank <= 3" 
                     :icon="getRankIcon(currentUserRank)" 
                     :class="getRankColor(currentUserRank)" 
                     class="tw-text-sm" />
             </div>
             <span class="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">{{ currentUser.name }}</span>
           </div>
           <span class="tw-text-sm tw-font-bold tw-text-blue-600">{{ currentUser.leads }}</span>
         </div>
        
        <!-- Top performers preview -->
        <div class="tw-text-xs tw-text-gray-500">
          <span>{{ $t('common.out-of') }} {{ totalUsers }} {{ $t('common.users') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores';
import { marketer } from '@/api/dashboard';

const authStore = useAuthStore();
const loading = ref(true);
const error = ref(null);

// API data - users ranking from server
const usersRankingData = ref([]);

// Process users data to hide names and identify current user
const processedUsers = computed(() => {
  if (!usersRankingData.value.length) return [];
  
  return usersRankingData.value.map((user, index) => ({
    ...user,
    displayName: user.id === authStore.user?.id ? user.name : `User #${index + 1}`,
    rank: index + 1,
    isCurrent: user.id === authStore.user?.id
  }));
});

// Current user data
const currentUser = computed(() => {
  return processedUsers.value.find(user => user.isCurrent)
   || {
    name: authStore.user?.name,
    leads: 0,
    rank: 1
  };
});

const currentUserRank = computed(() => currentUser.value.rank || 2);
const totalUsers = computed(() => processedUsers.value.length);

// Helper functions for rank styling
const getRankIcon = (rank) => {
  switch(rank) {
    case 1: return 'fluent:trophy-24-filled';
    case 2: return 'fluent:trophy-24-filled';
    case 3: return 'fluent:trophy-24-filled';
    default: return '';
  }
};

const getRankColor = (rank) => {
  switch(rank) {
    case 1: return 'tw-text-amber-500';
    case 2: return 'tw-text-gray-400';
    case 3: return 'tw-text-orange-400';
    default: return 'tw-text-gray-600';
  }
};

// Fetch users ranking data from API
const getData = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await marketer.usersRanking();
    
    // API returns direct array of users
    if (Array.isArray(response.data)) {
      usersRankingData.value = response.data;
    } else {
      error.value = 'Invalid response format';
    }
  } catch (err) {
    console.error('Error fetching users ranking:', err);
    error.value = 'An error occurred while loading users ranking';
    
    // Fallback to demo data on error
    usersRankingData.value = [
    //   { id: 1, name: 'John Doe', leads: 850 },
    //   { id: authStore.user?.id || 2, name: authStore.user?.name || 'Current User', leads: 720 },
    //   { id: 3, name: 'Mike Johnson', leads: 680 },
    //   { id: 4, name: 'Sarah Wilson', leads: 620 },
    //   { id: 5, name: 'Alex Brown', leads: 580 }
    ];
  } finally {
    loading.value = false;
  }
};

// Initialize data loading
getData();
</script>

<style></style> 